<template>
  <div class="units-settings">
    <Loader
      :value="promises.fetchOne"
      :message="`${this.$t('loading', { resource: this.$t('dashboard').toLowerCase() })}...`"
      class="dashboard"
    >
      <ModalComponent :show="Boolean(variant)" @close="$emit('close')" :clickaway="true">
        <Card
          class="units-settings__content"
          variant="prompt"
          :title="$t(`unitsAdaptationsTitle${variantCapitalized}`)"
          :subtitle="$t(`unitsAdaptationsSubtitle${variantCapitalized}`)"
        >
          <template #default>
            <h2 class="units-settings__content-title">
              {{ $t(`unitsAdaptationsContentTitle${variantCapitalized}`) }}
            </h2>
            <Properties
              :title="$t('basisAndKeyInfo')"
              :value="items.property"
              @input="$ev => update($ev, 'property')"
              :enable-sort="enableSorting('property')"
              :variant="variant"
            >
            </Properties>
            <Properties
              :title="$tc('facility', 2)"
              :value="items.facility"
              @input="$ev => update($ev, 'facility')"
              :enable-sort="enableSorting('facility')"
              type="facility"
              :variant="variant"
            >
            </Properties>
          </template>

          <template #actions>
            <ButtonComponent :label="$t('cancel')" @click.stop="reset" :icon="['fal', 'times']" />
            <ButtonComponent
              :promise="promise"
              theme="primary"
              :label="$t('confirm')"
              :disabled="!hasChanges"
              @click.stop="submit"
              :icon="['fal', 'arrow-right']"
            />
          </template>
        </Card>
      </ModalComponent>
    </Loader>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { ModalComponent } from 'vue-elder-modal'
import Properties from './Properties.vue'

import Card from '@/components/Card-v2'
import { MountingPortal } from 'portal-vue'
import { mergeDeepRight } from 'ramda'
import { SortByKey, Capitalize } from '@/utils'
import Bus from '@/bus'

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    useValue: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: '',
      enums: ['order', 'list'],
    },
  },
  data() {
    return {
      data: null,
      promise: null,
      items: {},
    }
  },
  watch: {
    variant: {
      handler(newVal) {
        if (newVal) {
          if (!this.data) {
            this.data = this.defaultData
            this.init()
          }
        } else this.data = null
      },
    },
  },
  computed: {
    ...mapState('Project', ['item', 'promises']),
    ...mapState(['enums']),

    residentialProperties() {
      if (!this.useValue) return this.$path('metadata.siteSettings.residentialProperties', this.item) || {}

      const base = this.enums.ResidentialProperties.reduce((acc, curr) => {
        return {
          ...acc,
          [curr.key]: {
            isFeatured: false,
            type: 'property',
            ...curr,
          },
        }
      }, {})

      if (!this.value) return base

      const merged = Object.keys(base).reduce((acc, key) => {
        acc[key] = {
          ...base[key],
          ...(this.value[key] || {}),
        }
        return acc
      }, {})

      return merged
    },
    defaultData() {
      if (this.useValue) return this.residentialProperties

      return Object.fromEntries(
        Object.entries(this.$path('metadata.siteSettings.allUsedResidentialProperties', this.item) || {}),
      )
    },

    draft() {
      return this.$path('item.draftId') || this.$path('item.metadata.draftAvailable')
    },

    hasActiveDraft() {
      return typeof this.draft === 'string'
    },

    hasChanges() {
      return this.data !== this.defaultData
    },
    variantCapitalized() {
      return Capitalize(this.variant)
    },
  },

  methods: {
    ...mapActions('Project', ['mutate']),
    init() {
      //build items
      const source = this.data

      this.items = Object.entries(source)
        .map(([key, v]) => ({ key, ...v }))
        .sort(SortByKey('order'))
        .reduce(
          (res, cur) => {
            res[cur.type].push(cur)
            return res
          },
          { property: [], facility: [] },
        )
    },
    reset() {
      this.data = null
      this.$emit('update:variant', '')
      if (Object.keys(this.$route.query).length) {
        this.$router.replace({ query: null })
      }
    },
    enableSorting(type) {
      return Boolean(Object.values(this.residentialProperties).find(i => i.type === type && i.order))
    },
    update(value, type) {
      this.items[type] = value

      let result = Object.values(this.items).reduce((res, cur) => {
        res.push(cur)
        return res
      }, [])

      this.data = Object.fromEntries(result.flat().map(i => [i.key, i]))
    },

    submit() {
      if (this.useValue) {
        this.$emit('input', this.data)

        return this.reset()
      }

      return (this.promise = this.mutate([
        {
          id: this.item.id,
          data: {
            metadata: {
              siteSettings: {
                residentialProperties: this.data,
              },
            },
          },
          getConfig: false,
        },
        {
          transformPayload: false,
          strategy: (item, payload) => {
            const { draftId, previewUrl, url, metadata } = payload
            const { siteSettings } = metadata
            const { allUsedResidentialProperties, residentialProperties } = siteSettings

            return mergeDeepRight(item, {
              url,
              previewUrl,
              draftId,
              metadata: {
                siteSettings: {
                  allUsedResidentialProperties: allUsedResidentialProperties,
                  residentialProperties: residentialProperties,
                },
              },
            })
          },
          draft: this.draft,
        },
      ]).then(v => {
        Bus.$emit('draft:update', v.draftId)

        this.reset()
      }))
    },
  },
  mounted() {
    if (this.$route.query.residentialsSettingsOpen) this.$emit('update:variant', 'list')
  },

  components: {
    ModalComponent,
    Card,
    Properties,
    MountingPortal,
  },
}
</script>

<style lang="scss">
.units-settings {
  &__content {
    $space: 2rem;
    width: fit-content;
    .card-v2 {
      &__content {
        margin: 1rem;
        padding: 0;
        border-radius: var(--border-radius);
        border: 1px solid var(--border-color);
        display: grid;
        grid-template-columns: repeat(2, 1fr);
      }
    }
    &-title {
      margin: 0;
      grid-column-end: span 2;
      border-bottom: 1px solid var(--border-color);
      padding: 1rem $space;
    }
    .settings-properties {
      padding: $space;
    }
  }
}
</style>
